import type { ApiClient } from "@ncp/api-client";
import { bi, type AgentCtx, type Bi } from "./engine";
import { day } from "./text";

/**
 * A memory of the Department's agentic reviews as /api/ec/memory returns it to a file's review: the file's own memories whole, and from
 * other files only the practice the Department set (method and reasoning), with no case number, party, officer or figure of that file.
 */
export interface StoredMemory { id: string; key: string; kind: "fact" | "preference" | "note"; text: string; textAr?: string | null; caseId?: string | null; caseNumber?: string | null; market?: string | null; parties?: string[]; agent?: string | null; by: string; at: string }

export const memoryApi = {
  list: (api: ApiClient, caseId: string) => api.get<StoredMemory[]>("/api/ec/memory", { caseId, take: 200 }),
};

/** A key part for a market or a party: lower case, words joined by dashes. */
export const slug = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

/** Practices reviewers set, keyed so a later file in the same market finds them. */
export const practiceKey = {
  shareBasis: (market: string) => `practice:share-basis:${slug(market)}`,
  marketDefinition: (market: string) => `practice:market-definition:${slug(market)}`,
  controlFromAgreement: () => "practice:control-from-agreement",
};

/** The Department's stored memories, loaded once per review by the memory agent (others wait for it a moment, then go without). */
export async function storeOf(ctx: AgentCtx, waitMs = 20_000): Promise<StoredMemory[]> {
  if (ctx.board.has("memory:store")) return ctx.board.get<StoredMemory[]>("memory:store") ?? [];
  let done = false;
  const value = ctx.board.need<StoredMemory[]>("memory:store").then((v) => { done = true; return v; });
  for (let waited = 0; waited < waitMs && !done; waited += 400) await ctx.wait(400);
  return done ? value : [];
}

/** Words that say nothing about who a company is or what a market sells (corporate forms, places, generic trades). */
const GENERIC = new Set(["emirates", "emirate", "group", "groups", "holding", "holdings", "company", "companies", "trading", "international", "national", "united",
  "middle", "general", "services", "service", "investment", "investments", "partners", "limited", "dubai", "sharjah", "ajman", "fujairah", "dhabi", "khaimah",
  "quwain", "industries", "enterprises", "establishment", "corporation", "formats", "products", "market", "markets"]);
export const words = (x?: string | null) => (x ?? "").toLowerCase().split(/[^a-z]+/).filter((t) => t.length > 4 && !GENERIC.has(t));
/** The same market: two distinctive words in common, or the same leading word ("Grocery retail (all formats)" and "Grocery retail – …"). */
export const sameMarket = (a?: string | null, b?: string | null) => {
  const wa = words(a), wb = words(b);
  if (!wa.length || !wb.length) return false;
  const bs = new Set(wb);
  return wa[0] === wb[0] || wa.filter((t) => bs.has(t)).length >= 2;
};

/** A practice the Department set, as opposed to a fact of one file. */
export const isPractice = (m: StoredMemory) => m.kind === "preference" && m.key.startsWith("practice:");

/**
 * A practice the Department set on another file: the same key, or, for a market's practice, the same practice in a market of the same kind
 * ("Grocery retail (all formats)" for "Grocery retail (supermarkets and hypermarkets)"). It is offered to the officer, never applied: the
 * agent puts it as a question, and the answer is the officer's (4.3, 4.5, 7.17).
 */
export function practiceIn(store: StoredMemory[], key: string, caseNumber: string, market?: string): StoredMemory | undefined {
  const other = store.filter((m) => isPractice(m) && m.caseNumber !== caseNumber);
  const exact = other.find((m) => m.key === key);
  if (exact || !market) return exact;
  const kind = `${key.split(":").slice(0, 2).join(":")}:`;
  return other.find((m) => m.key.startsWith(kind) && sameMarket(m.market, market));
}

/** Where a practice comes from, without identifying the file it was set on: "Department practice, recorded 23 October 2026". */
export function provenance(m: StoredMemory): Bi {
  const when = day(m.at);
  return bi(`Department practice, recorded ${when.en}`, `ممارسة الإدارة، سُجلت في ${when.ar}`);
}
